import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import Layout from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Calendar, User, Share2, Tag, Eye } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";

const BLOG_IMAGE = "https://images.pexels.com/photos/7723603/pexels-photo-7723603.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function BlogDetailPage() {
  const { blogId } = useParams();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBlog();
  }, [blogId]);

  const fetchBlog = async () => {
    try {
      const response = await axios.get(`${API}/blog/${blogId}`);
      setBlog(response.data);
    } catch (error) {
      console.error("Error fetching blog:", error);
      toast.error("Article not found");
    } finally {
      setLoading(false);
    }
  };

  const handleShare = async () => {
    if (navigator.share) {
      await navigator.share({
        title: blog.title,
        text: blog.excerpt || blog.content.substring(0, 200),
        url: window.location.href
      });
    } else {
      try { await navigator.clipboard.writeText(window.location.href); toast.success("Link copied to clipboard!"); }
      catch { toast.error("Clipboard unavailable — long-press the URL to copy"); }
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="container-custom py-16">
          <div className="animate-pulse space-y-8 max-w-3xl mx-auto">
            <div className="h-8 bg-slate-200 rounded w-3/4"></div>
            <div className="h-64 bg-slate-200 rounded"></div>
            <div className="space-y-4">
              <div className="h-4 bg-slate-200 rounded"></div>
              <div className="h-4 bg-slate-200 rounded"></div>
              <div className="h-4 bg-slate-200 rounded w-5/6"></div>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  if (!blog) {
    return (
      <Layout>
        <div className="container-custom py-16 text-center">
          <h1 className="text-2xl font-bold text-slate-700">Article Not Found</h1>
          <Link to="/blog" className="text-awareness-blue mt-4 inline-block">
            Back to Blog
          </Link>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Hero Image */}
      <div className="relative h-[40vh] md:h-[50vh]" data-testid="blog-hero">
        <img 
          src={blog.media?.[0]?.url || blog.image_url || BLOG_IMAGE}
          alt={blog.title}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
        <div className="absolute bottom-0 left-0 right-0 p-8">
          <div className="container-custom max-w-3xl">
            <Link to="/blog" className="inline-flex items-center text-white/80 hover:text-white mb-4" data-testid="back-to-blog">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Blog
            </Link>
            <span className="inline-block bg-awareness-blue text-deep-blue text-sm font-semibold px-4 py-1 rounded-full mb-4">
              {blog.category}
            </span>
            <h1 className="text-3xl md:text-5xl font-bold text-white font-heading">{blog.title}</h1>
          </div>
        </div>
      </div>

      {/* Article Content */}
      <article className="section-spacing" data-testid="blog-content">
        <div className="container-custom max-w-3xl">
          {/* Meta */}
          <div className="flex flex-wrap items-center gap-6 text-slate-500 text-sm mb-8 pb-8 border-b border-slate-200">
            <span className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              {format(new Date(blog.created_at), "MMMM d, yyyy")}
            </span>
            {blog.author_name && (
              <span className="flex items-center gap-2">
                <User className="w-4 h-4" />
                {blog.author_name}
              </span>
            )}
            <span className="flex items-center gap-2">
              <Eye className="w-4 h-4" />
              {blog.views} views
            </span>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={handleShare}
              className="ml-auto"
              data-testid="share-blog-btn"
            >
              <Share2 className="w-4 h-4 mr-2" />
              Share
            </Button>
          </div>

          {/* Content */}
          <div className="prose prose-lg max-w-none">
            {blog.content.split('\n').map((paragraph, index) => (
              paragraph.trim() && (
                <p key={index} className="text-slate-600 leading-relaxed mb-6">
                  {paragraph}
                </p>
              )
            ))}
          </div>

          {/* Tags */}
          {blog.tags && blog.tags.length > 0 && (
            <div className="mt-12 pt-8 border-t border-slate-200" data-testid="blog-tags">
              <div className="flex items-center gap-3 flex-wrap">
                <Tag className="w-4 h-4 text-slate-400" />
                {blog.tags.map((tag, index) => (
                  <span 
                    key={index}
                    className="bg-slate-100 text-slate-600 px-3 py-1 rounded-full text-sm"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* CTA */}
          <div className="mt-12 bg-slate-50 p-8 rounded-lg text-center">
            <h3 className="text-heading-3 text-deep-blue mb-4">Support Our Mission</h3>
            <p className="text-slate-600 mb-6">
              Help us continue spreading prostate cancer awareness to first responders and communities.
            </p>
            <div className="flex justify-center gap-4">
              <Link to="/donate">
                <Button className="btn-primary" data-testid="blog-donate-btn">Donate Now</Button>
              </Link>
              <Link to="/get-involved">
                <Button variant="outline" data-testid="blog-involved-btn">Get Involved</Button>
              </Link>
            </div>
          </div>
        </div>
      </article>
    </Layout>
  );
}
